import * as w from '@packages/systems/wfdl';
import * as wast from '@packages/systems/wfdl-ast';
import {weakMemo} from '@packages/utilities/memo';
import type {Form} from '@packages/systems/flows/types';
import {call1, call2, $, _$, at, call} from '@packages/utilities/wfdl-helpers';
import {pure, fail, bind} from '@packages/systems/plugins/Effect';
import {concat, isEmpty, textEquals} from '@packages/systems/plugins/Text';

const formDataVar = $('formData');
const maybeGetFieldVar = _$('Logic', 'Maybe get field');
const traverseListVar = _$('Effect', 'Traverse List');
const parseTextFromJsonVar = _$('Effect', 'Parse Text from Json');
const parseTextFromJson = call1(parseTextFromJsonVar);
const parseListFromJson = call1(_$('Effect', 'Parse List from Json'));
const expectEmailVar = _$('Form Logic', 'Expect Email');
const expectNumberVar = _$('Form Logic', 'Expect Number');
const expectBooleanVar = _$('Form Logic', 'Expect Boolean');
const expectLinkVar = _$('Form Logic', 'Expect Link');

const someIdentity = wast.EFunction({
  param: 'x',
  body: pure(
    wast.EUnion({
      tag: 'Some',
      value: $('x'),
    })
  ),
});

// @ts-expect-error - TS7031 - Binding element 'cases' implicitly has an 'any' type. | TS7031 - Binding element 'field' implicitly has an 'any' type. | TS7031 - Binding element 'param' implicitly has an 'any' type. | TS7031 - Binding element 'body' implicitly has an 'any' type.
function bindCases({cases, field, param, body}) {
  return bind(wast.ECase({value: field, cases}), wast.EFunction({param, body}));
}

const noneNotRequired = wast.EFunction({
  param: '_',
  body: pure(
    wast.EUnion({
      tag: 'None',
      value: wast.ERecord({}),
    })
  ),
});

export const generateFormParser = weakMemo(function generateFormParser(
  form: Form
): wast.Expression {
  const initialBody = pure(
    wast.ERecord({
      'Form fields': wast.ERecord(
        form.fields.reduce<Record<string, any>>(
          (res, {fieldName, fieldType}) => {
            // We used to collect password fields too in the past
            if (fieldType.tag !== 'Password') {
              res[fieldName] = $(fieldName);
            }
            return res;
          },
          {}
        )
      ),
      Timestamp: at(formDataVar, ['Timestamp']),
    })
  );

  const output = form.fields.reduce((acc, field) => {
    const {fieldType} = field;
    const maybeField = w.Expressions._call(maybeGetFieldVar, [
      wast.EText(field.fieldName),
      at(formDataVar, ['Form fields']),
    ]);

    const noneRequired = wast.EFunction({
      param: '_',
      body: fail(
        wast.EUnion({
          tag: 'Missing form field',
          value: wast.EText(
            `Failed to parse type: "${fieldType.tag}", field: "${field.fieldName}", form: "${form.name}".`
          ),
        })
      ),
    });

    if (
      fieldType.tag === 'Plain' ||
      //
      // Note: In general, password fields are only available on USYS pages
      // (login, signup, update, reset) and password protected pages. Forms
      // aren't actually allowed to be submitted if there is a password
      // field anywhere else, so we exclude being able to bind to a password
      // field.
      //
      // fieldType.tag === 'Password' ||

      fieldType.tag === 'Phone'
    ) {
      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: parseTextFromJson($('value')),
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(
                  parseTextFromJson($('value')),
                  wast.EFunction({
                    param: 'text',
                    body: wast.ECase({
                      value: isEmpty($('text')),
                      cases: {
                        True: noneNotRequired,
                        False: wast.EFunction({
                          param: '_',
                          body: pure(
                            wast.EUnion({
                              tag: 'Some',
                              value: $('text'),
                            })
                          ),
                        }),
                      },
                    }),
                  })
                ),
              }),
              None: noneNotRequired,
            },
      });
    }

    if (fieldType.tag === 'Email') {
      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: bind(parseTextFromJson($('value')), expectEmailVar),
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(
                  parseTextFromJson($('value')),
                  wast.EFunction({
                    param: 'text',
                    body: wast.ECase({
                      value: isEmpty($('text')),
                      cases: {
                        True: noneNotRequired,
                        False: wast.EFunction({
                          param: '_',
                          body: bind(
                            call(expectEmailVar, [$('text')]),
                            someIdentity
                          ),
                        }),
                      },
                    }),
                  })
                ),
              }),
              None: noneNotRequired,
            },
      });
    }

    if (fieldType.tag === 'Number') {
      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: bind(parseTextFromJson($('value')), expectNumberVar),
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(
                  parseTextFromJson($('value')),
                  wast.EFunction({
                    param: 'text',
                    body: wast.ECase({
                      value: isEmpty($('text')),
                      cases: {
                        True: noneNotRequired,
                        False: wast.EFunction({
                          param: '_',
                          body: bind(
                            call(expectNumberVar, [$('text')]),
                            someIdentity
                          ),
                        }),
                      },
                    }),
                  })
                ),
              }),
              None: noneNotRequired,
            },
      });
    }

    if (fieldType.tag === 'Checkbox') {
      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: {
          Some: wast.EFunction({
            param: 'value',
            body: bind(parseTextFromJson($('value')), expectBooleanVar),
          }),
          None: noneRequired,
        },
      });
    }

    if (fieldType.tag === 'Select') {
      let parseSelectArg;

      if (fieldType.val.allowMultiple) {
        parseSelectArg = bind(
          bind(
            parseListFromJson($('value')),
            wast.ECall({fun: traverseListVar, arg: parseTextFromJsonVar})
          ),
          wast.EFunction({
            param: 'options',
            body: pure(
              wast.ERecord(
                fieldType.val.choices.reduce<Record<string, any>>(
                  (result, choice) => {
                    result[choice] = call2(_$('Logic', 'List Includes'))(
                      $('options'),
                      wast.EText(choice)
                    );

                    return result;
                  },
                  {}
                )
              )
            ),
          })
        );
      } else {
        parseSelectArg = bind(
          parseTextFromJson($('value')),
          wast.EFunction({
            param: 'text',
            body: fieldType.val.choices.reduce(
              (next, choice) => {
                return wast.ECase({
                  value: textEquals($('text'), wast.EText(choice)),
                  cases: {
                    True: wast.EFunction({
                      param: '_',
                      body: pure(
                        wast.EUnion({
                          tag: choice,
                          value: wast.ERecord({}),
                        })
                      ),
                    }),
                    False: wast.EFunction({
                      param: '_',
                      body: next,
                    }),
                  },
                });
              },

              fail(
                wast.EUnion({
                  tag: 'Form Field Parse Error',
                  value: concat([
                    wast.EText('Could not parse select value from "'),
                    $('text'),
                    wast.EText('".'),
                  ]),
                })
              )
            ),
          })
        );
      }

      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: parseSelectArg,
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(parseSelectArg, someIdentity),
              }),
              None: noneNotRequired,
            },
      });
    }

    if (fieldType.tag === 'Radio') {
      const option = bind(
        parseTextFromJson($('value')),
        wast.EFunction({
          param: 'text',
          body: fieldType.val.choices.reduce(
            (next, choice) => {
              return wast.ECase({
                value: textEquals($('text'), wast.EText(choice)),
                cases: {
                  True: wast.EFunction({
                    param: '_',
                    body: pure(
                      wast.EUnion({
                        tag: choice,
                        value: wast.ERecord({}),
                      })
                    ),
                  }),
                  False: wast.EFunction({
                    param: '_',
                    body: next,
                  }),
                },
              });
            },
            fail(
              wast.EUnion({
                tag: 'Form Field Parse Error',
                value: concat([
                  wast.EText('Could not parse radio value from "'),
                  $('text'),
                  wast.EText('".'),
                ]),
              })
            )
          ),
        })
      );

      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: option,
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(option, someIdentity),
              }),
              None: noneNotRequired,
            },
      });
    }

    if (fieldType.tag === 'File') {
      return bindCases({
        field: maybeField,
        param: field.fieldName,
        body: acc,
        cases: field.required
          ? {
              Some: wast.EFunction({
                param: 'value',
                body: bind(parseTextFromJson($('value')), expectLinkVar),
              }),
              None: noneRequired,
            }
          : {
              Some: wast.EFunction({
                param: 'value',
                body: bind(
                  bind(parseTextFromJson($('value')), expectLinkVar),
                  someIdentity
                ),
              }),
              None: noneNotRequired,
            },
      });
    }

    return acc;
  }, initialBody);

  return wast.EFunction({param: 'formData', body: output});
});
